import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { portalApi } from '../api/portal';
import { Button, Input, Card, Spinner } from '../components/serac';
import MarketingHeader from '../components/common/MarketingHeader';
import MarketingFooter from '../components/common/MarketingFooter';
import Turnstile, { useTurnstile } from '../components/common/Turnstile';
import toast from 'react-hot-toast';

type InviteState =
  | 'loading'
  | 'requires_auth'
  | 'requires_registration'
  | 'verification_sent'
  | 'accepted'
  | 'error'
  | 'expired';

interface InviteInfo {
  organizationName?: string;
  organizationId?: number;
  role?: string;
  inviteEmail?: string;
}

export default function InviteAcceptPage() {
  const { t } = useTranslation(['auth', 'common']);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    portalLogin,
    portalRegister,
    isPortalAuthenticated,
    portalSession,
  } = useAuth();
  const { branding } = useBranding();
  const turnstile = useTurnstile();

  const token = searchParams.get('token');

  const [inviteState, setInviteState] = useState<InviteState>('loading');
  const [inviteInfo, setInviteInfo] = useState<InviteInfo>({});
  const [errorMessage, setErrorMessage] = useState('');

  // Login form state
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);

  // Check invite status on mount
  useEffect(() => {
    if (!token) {
      setInviteState('error');
      setErrorMessage(t('auth:invite.missingToken'));
      return;
    }

    checkInviteStatus();
  }, [token]);

  // If user becomes authenticated, try to accept the invite
  useEffect(() => {
    if (isPortalAuthenticated && token && inviteState === 'requires_auth') {
      acceptInvite();
    }
  }, [isPortalAuthenticated, token, inviteState]);

  async function checkInviteStatus() {
    if (!token) return;

    setInviteState('loading');
    try {
      const result = await portalApi.acceptInvite(token);

      if (result.success) {
        // Invite accepted successfully
        setInviteInfo({
          organizationName: result.organization?.name,
          role: result.role,
        });
        setInviteState('accepted');
        toast.success(t('auth:invite.addedToTeam'));

        // Redirect to dashboard after short delay
        setTimeout(() => {
          navigate('/portal/dashboard');
        }, 2000);
      } else if (result.requiresAuth) {
        // User needs to login or register first
        setInviteInfo({
          organizationName: result.organization?.name,
          organizationId: result.organization?.id,
          role: result.role,
          inviteEmail: result.inviteEmail,
        });
        setEmail(result.inviteEmail || '');
        setInviteState('requires_auth');
      }
    } catch (error: any) {
      console.error('Error checking invite:', error);

      const errorMsg = error.response?.data?.error || error.message || 'Unknown error';

      if (errorMsg.includes('expired')) {
        setInviteState('expired');
        setErrorMessage(t('auth:invite.expired'));
      } else if (errorMsg.includes('not found')) {
        setInviteState('error');
        setErrorMessage(t('auth:invite.invalid'));
      } else {
        setInviteState('error');
        setErrorMessage(errorMsg);
      }
    }
  }

  async function acceptInvite() {
    if (!token) return;

    setIsLoading(true);
    try {
      const result = await portalApi.acceptInvite(token);

      if (result.success) {
        setInviteInfo({
          organizationName: result.organization?.name,
          role: result.role,
        });
        setInviteState('accepted');
        toast.success(t('auth:invite.addedToTeam'));

        setTimeout(() => {
          navigate('/portal/dashboard');
        }, 2000);
      } else {
        setInviteState('error');
        setErrorMessage(t('auth:invite.couldNotAccept'));
      }
    } catch (error: any) {
      console.error('Error accepting invite:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Unknown error';

      if (errorMsg.includes('email') && errorMsg.includes('match')) {
        setInviteState('error');
        setErrorMessage(t('auth:invite.emailMismatch'));
      } else {
        setInviteState('error');
        setErrorMessage(errorMsg);
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();

    if (!turnstile.isReady) {
      toast.error(t('auth:invite.captchaRequired'));
      return;
    }

    setIsLoading(true);

    try {
      await portalLogin(email, password, turnstile.token);
      // The useEffect will handle accepting the invite after authentication
    } catch (error: any) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.error || t('auth:invite.loginFailed'));
      turnstile.reset();
    } finally {
      setIsLoading(false);
    }
  }

  async function handleRegister(e: React.FormEvent) {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error(t('auth:invite.passwordsMismatch'));
      return;
    }

    if (password.length < 8) {
      toast.error(t('auth:invite.passwordTooShort'));
      return;
    }

    if (!turnstile.isReady) {
      toast.error(t('auth:invite.captchaRequired'));
      return;
    }

    setIsLoading(true);

    try {
      // Register as individual — the backend detects the pending invite on this email
      // and skips org creation. After email verification, membership is activated.
      await portalRegister(email, password, name, 'individual', undefined, undefined, turnstile.token);

      setInviteState('verification_sent');
    } catch (error: any) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.error || t('auth:invite.registrationFailed'));
      turnstile.reset();
    } finally {
      setIsLoading(false);
    }
  }

  function getRoleDisplayName(role?: string): string {
    switch (role) {
      case 'admin':
        return t('auth:invite.roles.admin');
      case 'developer':
        return t('auth:invite.roles.developer');
      case 'stakeholder':
        return t('auth:invite.roles.stakeholder');
      default:
        return role || t('auth:invite.roles.default');
    }
  }

  // Loading state
  if (inviteState === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex flex-col">
        <MarketingHeader />
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="max-w-md w-full p-8 text-center">
            <div className="flex justify-center mb-4">
              <Spinner size="lg" />
            </div>
            <p className="text-gray-600">{t('auth:invite.checking')}</p>
          </Card>
        </div>
        <MarketingFooter />
      </div>
    );
  }

  // Error state
  if (inviteState === 'error' || inviteState === 'expired') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex flex-col">
        <MarketingHeader />
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="max-w-md w-full p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {inviteState === 'expired' ? t('auth:invite.expiredTitle') : t('auth:invite.invalidTitle')}
            </h1>
            <p className="text-gray-600 mb-6">{errorMessage}</p>
            <Link to="/">
              <Button variant="ice">{t('auth:invite.backToHome')}</Button>
            </Link>
          </Card>
        </div>
        <MarketingFooter />
      </div>
    );
  }

  // Success state
  if (inviteState === 'accepted') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex flex-col">
        <MarketingHeader />
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="max-w-md w-full p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('auth:invite.welcomeTitle')}</h1>
            <p className="text-gray-600 mb-2">
              {t('auth:invite.welcomeBodyPrefix')} <strong>{inviteInfo.organizationName || t('auth:invite.roles.default')}</strong>
            </p>
            <p className="text-sm text-gray-500 mb-6">
              {t('auth:invite.asRole', { role: getRoleDisplayName(inviteInfo.role) })}
            </p>
            <p className="text-sm text-gray-400">{t('auth:invite.redirecting')}</p>
          </Card>
        </div>
        <MarketingFooter />
      </div>
    );
  }

  // Verification sent state - shown after successful registration
  if (inviteState === 'verification_sent') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex flex-col">
        <MarketingHeader />
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="max-w-md w-full p-8 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('auth:invite.checkEmailTitle')}</h1>
            <p className="text-gray-600 mb-2">
              {t('auth:invite.verificationLinkSentPrefix')} <strong>{email}</strong>
            </p>
            <p className="text-sm text-gray-500 mb-6">
              {t('auth:invite.verificationFooterPrefix')}{' '}
              <strong>{inviteInfo.organizationName || t('auth:invite.roles.default')}</strong> {t('auth:invite.verificationFooterAs')}{' '}
              {getRoleDisplayName(inviteInfo.role)}.
            </p>
            <Link to="/">
              <Button variant="quiet">{t('auth:invite.backToHome')}</Button>
            </Link>
          </Card>
        </div>
        <MarketingFooter />
      </div>
    );
  }

  // Requires authentication state - show login/register options
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex flex-col">
      <MarketingHeader />
      <div className="flex-1 flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-8">
        {/* Invite info */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('auth:invite.youAreInvited')}</h1>
          <p className="text-gray-600">
            {t('auth:invite.invitedToJoin')}{' '}
            <strong className="text-gray-900">{inviteInfo.organizationName || t('auth:invite.roles.default')}</strong>
          </p>
          <p className="text-sm text-blue-600 mt-1">
            {t('auth:invite.asRole', { role: getRoleDisplayName(inviteInfo.role) })}
          </p>
        </div>

        {/* Login/Register Form */}
        {showLoginForm ? (
          <form onSubmit={isRegistering ? handleRegister : handleLogin} className="space-y-4">
            {isRegistering && (
              <Input
                label={t('auth:invite.fields.name')}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('auth:invite.fields.namePlaceholder')}
                required
              />
            )}

            <Input
              label={t('auth:invite.fields.email')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('auth:invite.fields.emailPlaceholder')}
              required
              disabled={!!inviteInfo.inviteEmail}
              helperText={inviteInfo.inviteEmail ? t('auth:invite.fields.emailHelper') : undefined}
            />

            <Input
              label={t('auth:invite.fields.password')}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={isRegistering ? t('auth:invite.fields.passwordRegisterPlaceholder') : t('auth:invite.fields.passwordLoginPlaceholder')}
              required
            />

            {isRegistering && (
              <Input
                label={t('auth:invite.fields.confirmPassword')}
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('auth:invite.fields.confirmPasswordPlaceholder')}
                required
              />
            )}

            <Turnstile
              action={isRegistering ? 'register' : 'login'}
              {...turnstile.handlers}
            />

            <Button
              type="submit"
              variant="ice"
              className="w-full"
              disabled={isLoading}
            >
              {isRegistering ? t('auth:invite.submitRegister') : t('auth:invite.submitLogin')}
            </Button>

            <div className="text-center">
              <button
                type="button"
                className="text-sm text-blue-600 hover:text-blue-700"
                onClick={() => {
                  setIsRegistering(!isRegistering);
                  setPassword('');
                  setConfirmPassword('');
                }}
              >
                {isRegistering ? t('auth:invite.switchToLogin') : t('auth:invite.switchToRegister')}
              </button>
            </div>

            <button
              type="button"
              className="w-full text-sm text-gray-500 hover:text-gray-700"
              onClick={() => setShowLoginForm(false)}
            >
              {t('auth:invite.back')}
            </button>
          </form>
        ) : (
          <div className="space-y-4">
            <Button
              variant="ice"
              className="w-full"
              onClick={() => {
                setShowLoginForm(true);
                setIsRegistering(false);
              }}
            >
              {t('auth:invite.signInExisting')}
            </Button>

            <Button
              variant="quiet"
              className="w-full"
              onClick={() => {
                setShowLoginForm(true);
                setIsRegistering(true);
              }}
            >
              {t('auth:invite.createNew')}
            </Button>

            <p className="text-xs text-gray-500 text-center mt-4">
              {t('auth:invite.sentTo')}{' '}
              <strong>{inviteInfo.inviteEmail}</strong>
            </p>
          </div>
        )}
      </Card>
      </div>
      <MarketingFooter />
    </div>
  );
}
